import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import { STORE } from "@/lib/config";
import { useSettings } from "@/context/SettingsContext";

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const { resolved } = useSettings();
  const heroImages = resolved.hero;

  useEffect(() => {
    if (!heroImages.length) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % heroImages.length), 5500);
    return () => clearInterval(t);
  }, [heroImages.length]);

  return (
    <section
      id="pocetna"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-neutral-900"
    >
      {/* SLIDESHOW with smooth crossfade + ken-burns */}
      <AnimatePresence mode="sync">
        {heroImages.length > 0 ? (
          <motion.div
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-0"
          >
            {/* [USER] HERO SLIKE: mijenjaju se preko Admin panela */}
            <div
              className="absolute inset-0 bg-cover bg-center kenburns"
              style={{ backgroundImage: `url(${heroImages[idx]})` }}
            />
            <div className="absolute inset-0 hero-gradient-overlay" />
          </motion.div>
        ) : (
          <motion.div
            key="brand-placeholder"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0"
          >
            {/* Branded loading placeholder — NEVER show a foreign image */}
            <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-[#3b0a0a] to-black" />
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 20% 30%, rgba(211,47,47,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 70%, rgba(211,47,47,0.3) 0%, transparent 50%)",
              }}
            />
            <div className="absolute inset-0 hero-gradient-overlay" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Red side bar accent */}
      <div className="absolute left-0 top-0 bottom-0 w-1 md:w-2 bg-[#D32F2F]" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-end pb-20 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="max-w-4xl"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 text-white text-xs uppercase tracking-[0.2em] mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#FCA5A5]" />
            Od {STORE.since}. — preko dva desetljeća s vama
          </div>

          <h1
            className="text-white text-5xl sm:text-6xl lg:text-8xl font-extrabold leading-[0.95] mb-6"
            style={{ fontFamily: "Cabinet Grotesk" }}
          >
            {STORE.name}
            <span className="block text-[#FCA5A5] text-2xl sm:text-3xl lg:text-4xl mt-3 font-medium tracking-wide">
              {STORE.city} · Tehnika & Knjižara
            </span>
          </h1>

          <p className="text-white/85 text-lg sm:text-xl max-w-2xl leading-relaxed mb-10">
            Mobiteli, laptopi, oprema, školski pribor i knjige — sve što vam treba, na jednom
            mjestu, sa povjerenjem stečenim kroz desetljeća.
          </p>

          <div className="flex flex-wrap gap-4">
            <a
              href="#proizvodi"
              data-testid="hero-products-btn"
              className="group inline-flex items-center gap-3 bg-[#D32F2F] hover:bg-[#B71C1C] text-white px-8 py-4 rounded-full font-semibold transition-all hover:shadow-2xl hover:shadow-red-500/30"
            >
              Pogledaj proizvode
              <ArrowDown className="w-4 h-4 group-hover:translate-y-1 transition-transform" />
            </a>
            <a
              href="#kontakt"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/30 hover:bg-white/20 text-white px-8 py-4 rounded-full font-semibold transition-all"
            >
              Kontakt
            </a>
          </div>
        </motion.div>

        {/* Slider indicators */}
        <div className="flex items-center gap-2 mt-12">
          {heroImages.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-indicator-${i}`}
              onClick={() => setIdx(i)}
              className={`h-1 rounded-full transition-all ${
                i === idx ? "w-12 bg-white" : "w-6 bg-white/40 hover:bg-white/60"
              }`}
              aria-label={`Slika ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
